/**
 * Hook for generating and previewing prescription label PDFs.
 * Manages loading state and error handling; callers pass assembled label
 * data directly to `print`.
 */
import { createElement, useCallback, useState } from 'react'
import { toast } from 'sonner'

import {
  MultiPrescriptionLabel,
  type PrescriptionLabelData,
} from '@/frontend/features/printing/labels/prescription-label.print'
import { previewPdf } from '@/frontend/features/printing/pdf-utils'

export type { PrescriptionLabelData }

/**
 * Provides a `print` function that generates a multi-page prescription label
 * PDF and opens it in a new tab. Owns `isPrinting` loading state.
 */
export function usePrintLabels() {
  const [isPrinting, setIsPrinting] = useState(false)

  const print = useCallback(async (labels: PrescriptionLabelData[], filename?: string) => {
    if (labels.length === 0) return
    setIsPrinting(true)
    try {
      const element = createElement(MultiPrescriptionLabel, { labels })
      await previewPdf(element, filename ?? `prescription-labels-${Date.now()}.pdf`)
    } catch {
      toast.error('Failed to generate labels')
    } finally {
      setIsPrinting(false)
    }
  }, [])

  return { print, isPrinting }
}
